import { Exchange } from 'grts-utils/types/Exchange';
import { Candle } from '../Detail/types';
import { BotTimeFrames, ComplexParams } from 'grts-utils/types/Bot';
import { TimeFrame } from '../../types/Bot';
import { OrderType } from 'grts-utils/types/Transaction';

export enum Tabs {
  Candles = 'Candles',
  SingleProfitCalculation = 'Single Profit Calculation',
  ComplexParamsCalcultion = 'Complex Params Calculation',
  CalculationResultHistory = 'Calculation Result History',
}

export enum ChartType {
  NoAlgoritmicProgression = 'NO_ALGORITMIC_PROGRESSION',
  StableBalance = 'STABLE_VALUE',
  AssetBalance = 'ASSET_VALUE',
}

export type Transaction = {
  index?: string;
  balanceInAsset: number;
  balanceInUsdt: number;
  assetQuantity: number;
  stableQuantity: number;
  currentFee: number;
  totalFee: number;
  date: string;
  id: number;
  isComplete: boolean;
  multiOrder: { index: number };
  orderPrice: number;
  orderType: 'BUY' | 'SELL';
  transactionIndex: number;
  profitInAsset: number;
  profitInUsdt: number;
  timestamp: number;
  transactionTimestamp: number;
  totalAmountOfFeesPaid: number;
  totalBalanceValue: number;
  transactionType: string;
  compactTransactionId: string;
  success: boolean;
  candles?: {
    higher: Candle[];
    shorter: Candle[];
    multi: Candle[];
  };
  signals?: IndicatorDataPoint[];
  exchange: Exchange;
  profit?: {
    profitSincePreviousTransaction: number;
    profitSincePreviousNonTpt: number;
    profitAllTptSincePreviousNonTpt: number;
    profitTotalBalance: number;
  };
  timeFrames?: BotTimeFrames | undefined;
  complexParams?: ComplexParams | undefined;
  rsiThreshold?: number;
  rsiValues?: IndicatorDataPoint[];
  takeProfitSize?: number;
  priceThreshold?: number;
  originalId?: number;
};

export type CompactTransaction = Transaction & {
  averageOrderPrice?: number | null;
  compactTransactionId: string;
};

export type SavedData = {
  higherTHeikinCandles: Candle[];
  higherTNormalCandles: Candle[];
  takeProfitTCandles: Candle[];
  shorterTNormalCandles: Candle[][];
  multiOrderCandles: Candle[][][];
};

export type CurrentProfit = {
  currentProfit: number;
  currentPriceChange: number;
};

export type CbrtRangeState = {
  isActive: boolean;
  open: number;
  direction: string;
  timestamp: number;
  startDate: string;
  endDate: string;
  endTimestamp?: number;
  cbrt: number;
};

export type MrcState = {
  startTimestamp: number;
  endTimestamp?: number;
  state: string;
  startPrice: number;
  endPrice?: number;
  orderType: string;
  timeFrame: TimeFrame;
};

export type WakeupTimes = {
  shorterT: number[];
  multiT: number[];
};

export type IndicatorDataPoint = {
  value: number;
  timestamp: number;
  price: number;
  signalType: 'BUY' | 'SELL';
};

export interface SingleProfitCalculationApiResponse {
  amountOfFailedEarlierOrders: number;
  amountOfFailedOrders: number;
  amountOfOrders: number;
  amountOfSuccessfulEarlierOrders: number;
  amountOfSuccessfulOrders: number;
  amountOfTotalFailedEarlierCalls: number;
  amountOfTotalSuccessfulEarlierCalls: number;
  amountOfTakeProfitOrders: number;
  amountOfStopLossOrders: number;
  baseAsset: string;
  exchange: Exchange;
  biggestGainInAsset: number;
  biggestGainInUsdt: number;
  biggestLossInAsset: number;
  biggestLossInUsdt: number;
  cbrt: number;
  currentCbrtRange: {
    isActive: boolean;
    open: number;
    direction: string;
    timestamp: number;
  };
  currentMrcState:
    | {
        isActive: boolean;
        direction: string;
        transactionType: string;
        state: 'INITIALISED' | 'PENDING' | 'RESET';
        orderType: 'BUY' | 'SELL';
        rejectedPrice: number;
        startTimestamp: number;
        timeFrame: TimeFrame;
      }
    | undefined;
  endBalanceInAsset: number;
  endBalanceInUsdt: number;
  endTimestamp: string;
  higherTimeFrame: string;
  takeProfitTimeFrame: string;
  maxDecimals: number;
  maxMultiOrderAmount: number;
  maxOrderSlippage: number;
  multiOrderTimeFrame: string;
  quoteAsset: string;
  savedData: SavedData;
  shorterTimeFrame: string;
  shorterTimeFrameInterval: number;
  smallestGainInAsset: number;
  smallestGainInUsdt: number;
  smallestLossInAsset: number;
  smallestLossInUsdt: number;
  startBalanceInAsset: number;
  startBalanceInUsdt: number;
  startTimestamp: string;
  supportCbrtRange: boolean;
  supportMrcState: boolean;
  totalAmountOfFeesPaid: number;
  totalTradingAssetProfit: number;
  totalTradingStableProfit: number;
  totalWithoutTradingStableProfit: number;
  totalWithoutTradingStableProfitSinceFirstTransaction: number;
  compactTransactions: CompactTransaction[];
  transactions: Transaction[];
  endBalanceInUsdtHistory: Record<string, number>;
  endBalanceInAssetHistory: Record<string, number>;
  priceHistory: Record<string, number>;
  orderPriceHistory: Record<string, number>;
  currentProfit: number;
  currentPriceChange: number;
  cbrtRangeStates: CbrtRangeState[];
  mrcStates: MrcState[];
  wakeupTimes: WakeupTimes;
  latestOrder: string;
  // Indicators
  useTakeProfitTransactions: boolean;
  indicatorConfig: IndicatorConfig;
  averageStableProfit: {
    daily: number;
    weekly: number;
    monthly: number;
    yearly: number;
  };
  averageAssetProfit: {
    daily: number;
    weekly: number;
    monthly: number;
    yearly: number;
  };
  stableProfitSince: {
    day: number;
    week: number;
    month: number;
    year: number;
    twoYear: number;
    FiveYear: number;
  };
  assetProfitSince: {
    day: number;
    week: number;
    month: number;
    year: number;
    twoYear: number;
    FiveYear: number;
  };
}

export interface RsiValuesApiResponse {
  higherT: IndicatorSingleValue[];
  takeProfitT: IndicatorSingleValue[];
  shorterT: IndicatorSingleValue[];
  multiT: IndicatorSingleValue[];
}

export interface ProfitResultIndicatorConfigs {
  higherT: IndicatorsConfig;
  takeProfitT: IndicatorsConfig;
  shorterT: IndicatorsConfig;
  multiT: IndicatorsConfig;
}

export interface ProfitResultIndicatorConfigs {
  higherT: IndicatorsConfig;
  takeProfitT: IndicatorsConfig;
  shorterT: IndicatorsConfig;
  multiT: IndicatorsConfig;
}

export type IndicatorSingleValue = {
  timestamp: number;
  value: number;
};

export type RSIItem = IndicatorSingleValue & {
  isOverExtended?: boolean;
  rsit?: number | undefined;
  price: number;
  signalType: OrderType;
};

export type MACD = {
  divergenceLine: IndicatorSingleValue[];
  signalLine: IndicatorSingleValue[];
  histogram: IndicatorSingleValue[];
  weightedHistogram: IndicatorSingleValue[];
};

export type Band = {
  upperBand: IndicatorSingleValue[];
  middleBand: IndicatorSingleValue[];
  lowerBand: IndicatorSingleValue[];
};

export type ZoningBand = {
  sellBand: IndicatorSingleValue[];
  upperBand: IndicatorSingleValue[];
  middleBand: IndicatorSingleValue[];
  lowerBand: IndicatorSingleValue[];
  buyBand: IndicatorSingleValue[];
};

export type BandConvergenceDivergence = {
  divergenceLine: IndicatorSingleValue[];
  signalLine: IndicatorSingleValue[];
  histogram: IndicatorSingleValue[];
  weightedHistogram: IndicatorSingleValue[];
};

export type MarketCipherWaves = {
  wave1: IndicatorSingleValue[];
  wave2: IndicatorSingleValue[];
};

export interface Indicators {
  rsi?: {
    standard?: IndicatorSingleValue[];
    stochastic?: IndicatorSingleValue[];
    connors?: IndicatorSingleValue[];
    waver?: {
      standard?: IndicatorSingleValue[][];
      stochastic?: IndicatorSingleValue[][];
      connors?: IndicatorSingleValue[][];
    };
  };
  ma?: {
    simple?: IndicatorSingleValue[];
    exponential?: IndicatorSingleValue[];
    macd?: MACD;
    priceOverExtension: IndicatorSingleValue[];
    waver?: {
      simple?: IndicatorSingleValue[][];
      exponential?: IndicatorSingleValue[][];
      priceOverExtension: IndicatorSingleValue[][];
    };
  };
  bands?: {
    bb?: {
      standard?: Band;
      zoning?: ZoningBand;
      bbcd?: BandConvergenceDivergence;
      overExtension: IndicatorSingleValue[];
    };
    keltner?: {
      standard?: Band;
      zoning?: ZoningBand;
      kccd?: BandConvergenceDivergence;
      overExtension: IndicatorSingleValue[];
    };
  };
  marketCipher?: {
    waves?: MarketCipherWaves;
  };
}

export interface IndicatorsConfig {
  rsi: {
    standard: {
      isEnabled: boolean;
      period: number | null;
      overExtensionThreshold: number | null;
    };
    stochastic: {
      isEnabled: boolean;
      period: number | null;
      overExtensionThreshold: number | null;
    };
    connors: {
      isEnabled: boolean;
      priceRSIPeriod: number | null;
      trendRSIPeriod: number | null;
      rateOfChangePeriod: number | null;
      overExtensionThreshold: number | null;
    };
    waver: {
      standard: {
        isEnabled: boolean;
        period: number | null;
        overExtensionThreshold: number | null;
      };
      stochastic: {
        isEnabled: boolean;
        period: number | null;
        overExtensionThreshold: number | null;
      };
    };
  };
  ma: {
    simple: {
      isEnabled: boolean;
      period: number | null;
    };
    exponential: {
      isEnabled: boolean;
      period: number | null;
    };
    macd: {
      isEnabled: boolean;
      shortPeriod: number | null;
      longPeriod: number | null;
      signalPeriod: number | null;
      showWeightedHistogram: boolean;
    };
    priceOverExtension: {
      isEnabled: boolean;
      divergencePeriod: number | null;
      rangePeriod: number | null;
      useExponantial: boolean;
      overExtensionThreshold: number | null;
    };
    waver: {
      simple: {
        isEnabled: boolean;
        periods: Array<number | null>;
      };
      exponential: {
        isEnabled: boolean;
        periods: Array<number | null>;
      };
      priceOverExtension: {
        isEnabled: boolean;
        divergencePeriod: number | null;
        rangePeriod: number | null;
        useExponantial: boolean | null;
        overExtensionThreshold: number | null;
      };
    };
  };
  bands: {
    bb: {
      overlay: {
        isEnabled: boolean;
        period: number | null;
        standardDeviationSize: number | null;
        zoningDeviationSize: number | null;
        showOverExtension: boolean;
      };
      bbcd: {
        isEnabled: boolean;
        period: number | null;
        standardDeviationSize: number | null;
        signalPeriod: number | null;
        showWeightedHistogram: boolean;
      };
    };
    keltner: {
      overlay: {
        isEnabled: boolean;
        period: number | null;
        averageTrueRangeMultiplier: number | null;
        zoningTrueRangeMultiplier: number | null;
        showOverExtension: boolean;
      };
      kccd: {
        isEnabled: boolean;
        period: number | null;
        averageTrueRangeMultiplier: number | null;
        signalPeriod: number | null;
        showWeightedHistogram: boolean;
      };
    };
  };
  marketCipher: {
    waves: {
      isEnabled: boolean;
      pricePeriod: number | null;
      priceDivergencePeriod: number | null;
      wavePeriod: number | null;
    };
  };
}

export const initialIndicatorsConfig: IndicatorsConfig = {
  rsi: {
    standard: {
      isEnabled: false,
      period: 7,
      overExtensionThreshold: 85,
    },
    stochastic: {
      isEnabled: true,
      period: 20,
      overExtensionThreshold: 85,
    },
    connors: {
      isEnabled: true,
      priceRSIPeriod: 3,
      trendRSIPeriod: 2,
      rateOfChangePeriod: 100,
      overExtensionThreshold: 90,
    },
    waver: {
      standard: {
        isEnabled: true,
        period: 7,
        overExtensionThreshold: 85,
      },
      stochastic: {
        isEnabled: false,
        period: 14,
        overExtensionThreshold: 85,
      },
    },
  },
  ma: {
    simple: {
      isEnabled: false,
      period: 9,
    },
    exponential: {
      isEnabled: false,
      period: 9,
    },
    macd: {
      isEnabled: false,
      shortPeriod: 12,
      longPeriod: 26,
      signalPeriod: 9,
      showWeightedHistogram: true,
    },
    priceOverExtension: {
      isEnabled: false,
      divergencePeriod: 10,
      rangePeriod: 18,
      useExponantial: false,
      overExtensionThreshold: 80,
    },
    waver: {
      simple: {
        isEnabled: false,
        periods: [20, 50, 100, 200],
      },
      exponential: {
        isEnabled: false,
        periods: [20, 50, 100, 200],
      },
      priceOverExtension: {
        isEnabled: false,
        divergencePeriod: 10,
        rangePeriod: 18,
        useExponantial: false,
        overExtensionThreshold: 80,
      },
    },
  },
  bands: {
    bb: {
      overlay: {
        isEnabled: true,
        period: 14,
        standardDeviationSize: 1.5,
        zoningDeviationSize: 2.5,
        showOverExtension: true,
      },
      bbcd: {
        isEnabled: false,
        period: 20,
        standardDeviationSize: 2,
        signalPeriod: 9,
        showWeightedHistogram: true,
      },
    },
    keltner: {
      overlay: {
        isEnabled: false,
        period: 20,
        averageTrueRangeMultiplier: 2,
        zoningTrueRangeMultiplier: 3,
        showOverExtension: true,
      },
      kccd: {
        isEnabled: false,
        period: 20,
        averageTrueRangeMultiplier: 2,
        signalPeriod: 9,
        showWeightedHistogram: true,
      },
    },
  },
  marketCipher: {
    waves: {
      isEnabled: true,
      pricePeriod: 10,
      priceDivergencePeriod: 21,
      wavePeriod: 3,
    },
  },
};

export interface IndicatorConfig {
  oppositeOrderTimeFrame: TimeFrame;
  oppositeOrderChildTimeFrame: TimeFrame;
  oppositeOrderPeriod: number;
  oppositeOrderThreshold: number;
  oppositeOrderCandleType: 'normal' | 'heikin-ashi';
  oppositeOrderTakeProfitSize: number;
  oppositeOrderStoplossThreshold: number;
  sameOrderTimeFrame: TimeFrame;
  sameOrderChildTimeFrame: TimeFrame;
  sameOrderPeriod: number;
  sameOrderThreshold: number;
  sameOrderCandleType: 'normal' | 'heikin-ashi';
  sameOrderTakeProfitSize: number;
  sameOrderStoplossThreshold: number;
}
